import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Gift, Loader2, Copy, Sparkles, TrendingUp, Ticket, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LoyaltyPanel = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState('');
  const [newVoucher, setNewVoucher] = useState(null);

  const fetchLoyalty = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/loyalty/me`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
      const convertible = Math.floor((r.data?.points || 0) / (r.data?.points_per_reward || 100)) * (r.data?.points_per_reward || 100);
      setSelectedPoints(convertible > 0 ? String(convertible) : '');
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLoyalty(); }, [fetchLoyalty]);

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>;
  }
  if (!data) return null;

  const { points, points_per_reward: step, reward_value: rewardValue } = data;
  const convertibleMax = Math.floor(points / step) * step;
  const options = [];
  for (let p = step; p <= convertibleMax; p += step) options.push(p);
  const progress = points % step;

  const convert = async () => {
    const pts = parseInt(selectedPoints);
    if (!pts) return;
    setConverting(true);
    try {
      const r = await axios.post(`${API}/loyalty/convert`, { points: pts }, { headers: { Authorization: `Bearer ${token}` } });
      setNewVoucher(r.data);
      toast.success(T(`Bon de ${r.data.value.toFixed(2)} € créé ✓`, `€${r.data.value.toFixed(2)} voucher created ✓`));
      fetchLoyalty();
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Erreur de conversion', 'Conversion error'));
    } finally {
      setConverting(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code).then(
      () => toast.success(T('Code copié !', 'Code copied!')),
      () => toast.info(code)
    );
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="loyalty-panel">
      {/* Balance */}
      <div className="bg-gradient-to-br from-[#0F4C5C] to-[#0B3A47] rounded-2xl p-6 md:p-8 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-white/70 text-sm flex items-center gap-2">
              <Gift className="w-4 h-4 text-[#E9C46A]" />
              {T('Mes points fidélité', 'My loyalty points')}
            </p>
            <p className="text-5xl font-extrabold mt-2 text-[#E9C46A]" data-testid="loyalty-points-balance">{points}</p>
            <p className="text-white/70 text-sm mt-2">
              {T(`1 € dépensé = 1 point · ${step} points = bon de ${rewardValue.toFixed(0)} €`,
                 `€1 spent = 1 point · ${step} points = €${rewardValue.toFixed(0)} voucher`)}
            </p>
            <p className="text-xs text-[#E9C46A] mt-1.5 flex items-center gap-1 font-medium" data-testid="loyalty-flash-bonus-note">
              <Zap className="w-3.5 h-3.5" />
              {T('Points doublés sur les articles en vente flash !', 'Double points on flash sale items!')}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-white/70">{T('Convertible', 'Convertible')}</p>
            <p className="text-2xl font-bold">{(convertibleMax / step * rewardValue).toFixed(0)} €</p>
          </div>
        </div>
        {convertibleMax < step && (
          <div className="mt-4">
            <div className="h-2 bg-white/15 rounded-full overflow-hidden">
              <div className="h-full bg-[#E9C46A] rounded-full transition-all" style={{ width: `${(progress / step) * 100}%` }} />
            </div>
            <p className="text-xs text-white/60 mt-1.5">
              {T(`Encore ${step - progress} points avant votre prochain bon de ${rewardValue.toFixed(0)} €`,
                 `${step - progress} more points until your next €${rewardValue.toFixed(0)} voucher`)}
            </p>
          </div>
        )}
      </div>

      {/* Convert */}
      <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 shadow-sm">
        <h3 className="font-bold text-[#0F4C5C] flex items-center gap-2 mb-1">
          <Sparkles className="w-5 h-5 text-[#E9C46A]" />
          {T('Convertir mes points', 'Convert my points')}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {T('Transformez vos points en bon de réduction utilisable sur toute la marketplace (valable 6 mois).',
             'Turn your points into a discount voucher valid across the whole marketplace (6 months validity).')}
        </p>
        {convertibleMax >= step ? (
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={selectedPoints} onValueChange={setSelectedPoints}>
              <SelectTrigger className="w-56" data-testid="loyalty-points-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((p) => (
                  <SelectItem key={p} value={String(p)}>
                    {p} {T('points', 'points')} → {(p / step * rewardValue).toFixed(0)} €
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={convert} disabled={converting || !selectedPoints} className="btn-primary" data-testid="loyalty-convert-btn">
              {converting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
              {T('Convertir', 'Convert')}
            </Button>
          </div>
        ) : (
          <p className="text-sm text-[#0F4C5C]/70 bg-[#FBF8F3] border border-dashed border-[#0F4C5C]/20 rounded-xl p-4" data-testid="loyalty-not-enough">
            {T(`Il vous faut au moins ${step} points pour convertir. Chaque commande vous rapproche de votre prochain bon !`,
               `You need at least ${step} points to convert. Every order gets you closer to your next voucher!`)}
          </p>
        )}
        {newVoucher && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="loyalty-new-voucher">
            <div>
              <p className="text-sm text-emerald-800 font-medium">
                {T(`Votre bon de ${newVoucher.value.toFixed(2)} € est prêt :`, `Your €${newVoucher.value.toFixed(2)} voucher is ready:`)}
              </p>
              <p className="font-mono text-lg font-bold text-emerald-900" data-testid="loyalty-voucher-code">{newVoucher.code}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => copyCode(newVoucher.code)} data-testid="loyalty-copy-code-btn">
              <Copy className="w-4 h-4 mr-1.5" />
              {T('Copier', 'Copy')}
            </Button>
          </div>
        )}
      </div>

      {/* My loyalty vouchers */}
      {data.vouchers?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 shadow-sm">
          <h3 className="font-bold text-[#0F4C5C] flex items-center gap-2 mb-4">
            <Ticket className="w-5 h-5 text-[#E9C46A]" />
            {T('Mes bons fidélité', 'My loyalty vouchers')}
          </h3>
          <div className="space-y-2" data-testid="loyalty-vouchers-list">
            {data.vouchers.map((v) => {
              const used = (v.usage_count || 0) >= (v.max_uses || 1);
              const expired = v.expires_at && new Date(v.expires_at) < new Date();
              return (
                <div key={v.id} className="flex items-center justify-between gap-3 bg-[#FBF8F3] rounded-xl px-4 py-3 border border-[#0F4C5C]/10">
                  <div className="min-w-0">
                    <p className="font-mono font-bold text-[#0F4C5C]">{v.code}</p>
                    <p className="text-xs text-muted-foreground">
                      −{Number(v.value).toFixed(2)} € · {v.expires_at
                        ? `${T('expire le', 'expires')} ${new Date(v.expires_at).toLocaleDateString()}`
                        : T('sans expiration', 'no expiry')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {used ? (
                      <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">{T('Utilisé', 'Used')}</Badge>
                    ) : expired ? (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{T('Expiré', 'Expired')}</Badge>
                    ) : (
                      <Badge className="badge-success">{T('Valide', 'Valid')}</Badge>
                    )}
                    {!used && !expired && (
                      <Button variant="ghost" size="icon" onClick={() => copyCode(v.code)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* History */}
      {data.history?.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-6 shadow-sm">
          <h3 className="font-bold text-[#0F4C5C] flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-[#E9C46A]" />
            {T('Historique des points', 'Points history')}
          </h3>
          <div className="space-y-1" data-testid="loyalty-history">
            {data.history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm py-2 border-b border-[#0F4C5C]/5 last:border-0">
                <span className="text-[#0F4C5C]/80">
                  {h.type === 'earned'
                    ? T(`Commande #${(h.order_id || '').slice(0, 8)}`, `Order #${(h.order_id || '').slice(0, 8)}`)
                    : T(`Conversion → ${h.voucher_code}`, `Converted → ${h.voucher_code}`)}
                  <span className="text-xs text-muted-foreground ml-2">{new Date(h.created_at).toLocaleDateString()}</span>
                </span>
                <span className={`font-semibold ${h.points > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {h.points > 0 ? `+${h.points}` : h.points} pts
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
